

import React, { useState, useEffect, useRef } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { AutocompleteChangeReason, AutocompleteChangeDetails } from '@mui/material/Autocomplete';


interface PlaceOption {
   label: string;
   place_id: string;
}

interface GooglePlacesSearchProps {
   searchType: 'places' | 'hotels';
   onPlaceSelect?: (placeName: string, lat?: number, lng?: number) => void;
   label: string;
    value?: any
    [key:string]:any
}

const GooglePlacesSearch: React.FC<GooglePlacesSearchProps> = ({
   searchType = 'places',
   onPlaceSelect,
   label,
    value,
   ...props
}) => {
   const [inputValue, setInputValue] = useState('');
   const [options, setOptions] = useState<PlaceOption[]>([]);
   const [loading, setLoading] = useState(false);
   const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null);
   const placesService = useRef<google.maps.places.PlacesService | null>(null);

   useEffect(() => {
      if (!window.google) {
         console.error('Google Maps JavaScript API is not loaded.');
         return;
      }
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
   }, []);

   const handleInputChange = (_event: React.SyntheticEvent, newInputValue: string) => {
      setInputValue(newInputValue);
      if (newInputValue.length > 1 && autocompleteService.current) {
         setLoading(true);
         autocompleteService.current.getPlacePredictions(
            { input: newInputValue },
            (predictions: any[] | null = []) => {
               setTimeout(() => {
                  setLoading(false);
               }, 500);

               if (predictions && predictions.length > 0) {
                  setOptions(
                     predictions.map((p) => ({ label: p.description, place_id: p.place_id }))
                  );
               } else {
                  setOptions([]);
               }
            }
         );
      } else {
         setOptions([]);
      }
   };

   const handleSelect = (
      _event: React.SyntheticEvent,
      newValue: string | PlaceOption | null,
      _reason: AutocompleteChangeReason,
      _details?: AutocompleteChangeDetails<PlaceOption> | undefined
   ) => {
      if (typeof newValue !== 'string' && newValue !== null) {
         const { place_id, label } = newValue;
         if (!window.google || !place_id) return;

         const map = new window.google.maps.Map(document.createElement('div'));
         placesService.current = new window.google.maps.places.PlacesService(map);

         const caller = placesService.current as any;

         caller.getDetails({ placeId: place_id }, (place: any, status: any) => {
            if (
               status === window.google.maps.places.PlacesServiceStatus.OK &&
               place?.geometry?.location
            ) {
               const lat = place.geometry.location.lat();
               const lng = place.geometry.location.lng();
               if (searchType === 'hotels') {
                  searchNearbyHotels(lat, lng);
               } else if (onPlaceSelect) {
                  onPlaceSelect(label, lat, lng);
               }
            }
         });
      }
   };

   const searchNearbyHotels = (lat: number, lng: number) => {
      const map = new window.google.maps.Map(document.createElement('div'));
      placesService.current = new window.google.maps.places.PlacesService(map);

      const caller = placesService.current as any;
      caller.search(
         {
            radius: 5000,
            types: ['lodging'],
            location: new google.maps.LatLng(lat, lng),
         },
         (results: any, status: any) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && results?.length) {
               const firstHotel = results[0];
               const lat = firstHotel.geometry?.location?.lat();
               const lng = firstHotel.geometry?.location?.lng();
               if (onPlaceSelect) {
                  onPlaceSelect(firstHotel.name, lat, lng);
               }
            } else {
               console.warn('No hotels found.');
            }
         }
      );
   };

   return (
      <Autocomplete
         {...props}
         options={[...options]}
         value={value}
         filterOptions={(options) => options}
         getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
         onInputChange={handleInputChange}
         onChange={handleSelect}
         loading={loading}
         freeSolo
         renderInput={(params) => (
            <TextField
               {...params}
               label={label}
               fullWidth
               margin='normal'
               InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                     <>
                        {loading ? <CircularProgress color='inherit' size={20} /> : null}
                        {params.InputProps.endAdornment}
                     </>
                  ),
               }}
            />
         )}
      />
   );
};

export default GooglePlacesSearch;

